import { type Message as AgUiMessage } from '@ag-ui/client'
import { type HydratedAgUiMessage } from 'shared/api/copilot'

/**
 * Whether to wipe client arrival stamps when `currentChatId` changes.
 * Preserve stamps for null → first server id (assigned mid-stream); clear on
 * real chat switches (including id → null for a new chat).
 */
export const shouldResetArrivalTimesForChatIdChange = (
  previousChatId: string | null,
  nextChatId: string | null
): boolean => {
  if (previousChatId === nextChatId) return false
  return !(previousChatId == null && nextChatId != null)
}

/**
 * When a live run settles (`wasLoading` → `!isLoading`), freeze wall-clock end
 * times for reasoning message ids that do not yet have one. The reducer uses
 * these so a reasoning-only / stopped turn does not snap from a live timer to
 * `0` (arrival stamp === end) on every later settled render.
 */
export const freezeReasoningEndTimesIfSettled = (
  messages: AgUiMessage[],
  endTimesById: Map<string, string>,
  options: { wasLoading: boolean; isLoading: boolean; now?: Date }
): void => {
  if (!options.wasLoading || options.isLoading) return
  const nowIso = (options.now ?? new Date()).toISOString()
  for (const message of messages) {
    if (message.role !== 'reasoning') continue
    if (!endTimesById.has(message.id)) {
      endTimesById.set(message.id, nowIso)
    }
  }
}

/**
 * Reasoning messages do not carry a duration. Stamp each message id with the
 * wall-clock of its first appearance so reducers can compute
 * `timeInSeconds` as (first reasoning) → (next non-reasoning / now).
 *
 * Prefers an existing string `createdAt` (hydrated history) and seeds the map
 * from it so later live updates keep a stable clock.
 */
export const stampMessageArrivalTimes = (
  messages: AgUiMessage[],
  arrivalTimesById: Map<string, string>,
  now: Date = new Date()
): HydratedAgUiMessage[] => {
  const nowIso = now.toISOString()

  return messages.map((message) => {
    const hydrated = message as HydratedAgUiMessage
    const existingCreatedAt =
      typeof hydrated.createdAt === 'string' && hydrated.createdAt.length > 0 ? hydrated.createdAt : null

    if (existingCreatedAt != null) {
      if (!arrivalTimesById.has(message.id)) {
        arrivalTimesById.set(message.id, existingCreatedAt)
      }
      return hydrated
    }

    const recorded = arrivalTimesById.get(message.id)
    if (recorded != null) {
      return { ...hydrated, createdAt: recorded }
    }

    arrivalTimesById.set(message.id, nowIso)
    return { ...hydrated, createdAt: nowIso }
  })
}
